import { Film } from 'lucide-react'
import * as React from 'react'

import { sanitizeHtmlValue } from '@/lib/element-schema-sanitizers'
import { cn } from '@/lib/utils'

import { CourseVideoPlayer } from '@/components/courses/course-video-player'
import { Skeleton } from '@/components/ui/skeleton'

import { getDisplayFileName } from '@/utils/display-file-name'

import type { ResourceType } from '@/types/resource.type'

function isDirectVideoUrl(value: string): boolean {
  const trimmed = value.trim()
  if (!trimmed || trimmed.includes('<')) return false
  try {
    const url = new URL(trimmed)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

const embedChildStyles = cn(
  '[&_iframe]:!absolute [&_iframe]:!inset-0 [&_iframe]:!h-full [&_iframe]:!w-full [&_iframe]:!max-w-none [&_iframe]:!border-0',
  '[&_video]:!absolute [&_video]:!inset-0 [&_video]:!h-full [&_video]:!w-full [&_video]:!object-contain',
  '[&>div]:!relative [&>div]:!h-full [&>div]:!w-full [&>div]:!max-w-none'
)

export function VideoContentViewer({ resource }: { resource: ResourceType }) {
  const playerRef = React.useRef<HTMLDivElement>(null)
  const [isLoaded, setIsLoaded] = React.useState(false)

  const title = getDisplayFileName(resource.fileName, 'Video')
  const source = resource.url?.trim() ?? ''
  const isUrl = isDirectVideoUrl(source)

  React.useEffect(() => {
    setIsLoaded(false)
    if (!source) {
      setIsLoaded(true)
      return
    }

    if (isUrl) return

    const markLoaded = () => setIsLoaded(true)
    const fallbackTimer = window.setTimeout(markLoaded, 4000)

    const attachMediaListeners = () => {
      const root = playerRef.current
      if (!root) return

      const iframe = root.querySelector('iframe')
      const video = root.querySelector('video')

      if (iframe) {
        iframe.addEventListener('load', markLoaded, { once: true })
        return
      }
      if (video) {
        video.addEventListener('canplay', markLoaded, { once: true })
        video.addEventListener('loadeddata', markLoaded, { once: true })
        return
      }
      markLoaded()
    }

    const attachTimer = window.setTimeout(attachMediaListeners, 100)

    return () => {
      window.clearTimeout(fallbackTimer)
      window.clearTimeout(attachTimer)
    }
  }, [isUrl, source])

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-b from-muted/50 via-background to-muted/30">
      <div className="mx-auto flex w-full max-w-5xl flex-1 flex-col px-4 py-8 md:px-6 md:py-12 lg:py-16">
        <header className="mb-6 md:mb-8">
          <div className="mb-2 flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Film className="size-4 shrink-0" aria-hidden />
            <span>Video resource</span>
          </div>
          <h1 className="text-balance text-2xl font-semibold tracking-tight text-foreground md:text-3xl">{title}</h1>
        </header>

        <div
          ref={playerRef}
          className="overflow-hidden rounded-2xl border border-border/60 bg-black shadow-2xl ring-1 ring-border/40"
        >
          <div className="relative aspect-video w-full bg-black">
            {!isLoaded && !isUrl ? (
              <Skeleton className="absolute inset-0 z-10 rounded-none bg-muted/40" aria-hidden />
            ) : null}
            {isUrl ? (
              <CourseVideoPlayer videoUrl={source} className="h-full w-full rounded-none" />
            ) : (
              <div
                data-resource-video-viewer
                className={cn('absolute inset-0 h-full w-full', embedChildStyles)}
                dangerouslySetInnerHTML={{ __html: sanitizeHtmlValue(source) }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
